// src/App.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useAuthStore } from "./store/authStore";

/* ---------- HOOKS ---------- */
import { useRouter } from "./hooks/useRouter";
import { useChat } from "./hooks/useChat";

/* ---------- COMPONENTS ---------- */
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import SplashScreen from "./components/SplashScreen";
import AuthModal from "./components/AuthModal";

/* ---------- PAGES ---------- */
import Home from "./pages/Home";
import Chat from "./pages/Chat";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import Business from "./pages/Business";
import News from "./pages/News";
import ProfileSettings from "./components/ProfileModal";

export default function App() {
  /* =========================================================
     1. CORE STATE & AUTH
     ========================================================= */
  const { setSession, clearSession, user } = useAuthStore();
  const isAuthed = !!user;
  const userGender = user?.user_metadata?.gender || 'Non-Binary';
  
  // Modal State for Login
  const [showAuth, setShowAuth] = useState(false);

  // Router Hook
  const {route, navigateTo, getRouteFromPath } = useRouter();
  
  // Chat Hook
  const {conversations, activeConvId, createNewConversation, openConversation, sendFromChat,deleteConversation } = useChat(navigateTo, route);

  const noScrollMode = route === "chat" || route === "home";
  /* =========================================================
     2. UI STATE
     ========================================================= */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [showSplash, setShowSplash] = useState(true);

  /* =========================================================
     3. EFFECTS & LISTENERS
     ========================================================= */
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      data?.session ? setSession(data.session) : clearSession();
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      session ? setSession(session) : clearSession();
    });

    return () => listener.subscription.unsubscribe();
  }, [setSession, clearSession]);

  useEffect(() => {
    if (route === "chat") {
      setSidebarCollapsed(false);
    } else {
      setSidebarCollapsed(true);
    }
  }, [route]);

  /* =========================================================
     4. HANDLERS
     ========================================================= */
  async function handleSignOut() {
    await supabase.auth.signOut();
    clearSession();
    navigateTo("/");
  }

  function handleSidebarToggle(explicit = null) {
    setSidebarCollapsed((prev) => 
      (explicit === null || explicit === undefined) ? !prev : Boolean(explicit)
    );
  }

  function handleNavigate(path) {
    const target = getRouteFromPath(path);
    if (target === "chat" && !isAuthed) {
       setShowAuth(true);
       return;
    }
    navigateTo(path);
  }

  function handleHomeStart(msg) {
    if (!isAuthed) {
      setShowAuth(true); 
      return;
    }
    sendFromChat(msg);}

  /* =========================================================
     5. STYLES & RENDER
     ========================================================= */
return (
    <div className="relative min-h-screen w-full bg-[#f7eccc] font-sans text-[#07102a]">
      
      {showSplash && (<SplashScreen onComplete={() => setShowSplash(false)} />)}

      {/* 1. HEADER: FIXED at Top, Full Width (Z-50). */}
      <div className="fixed top-0 left-0 right-0 h-16 z-50 bg-[#f7eccc] hidden md:block">
        <Header 
          navigateTo={handleNavigate} 
          handleSignOut={handleSignOut}
          userGender={userGender}
          onOpenAuth={() => setShowAuth(true)}
        />
      </div>

      <button 
        className="fixed top-4 left-4 z-50 block md:hidden p-2 bg-[#faf2d9] rounded-full shadow-md text-[#07102a]"
        onClick={() => handleSidebarToggle(false)}
      >
        {/* Simple Hamburger Icon */}
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* 2. SIDEBAR: FIXED, Below Header (Z-40) */}
      <Sidebar
        isAuthed={isAuthed}
        conversations={conversations}
        activeConvId={activeConvId}
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={handleSidebarToggle}
        onNewConversation={() => createNewConversation(setShowAuth)} 
        onOpenConversation={openConversation}
        onDeleteConversation={deleteConversation}
      />

      {/* 3. MAIN CONTENT WRAPPER */}
      <div className={`
          flex flex-col h-screen w-full pt-28
          transition-[padding-left] duration-300 ease-in-out pl-0
          ${sidebarCollapsed ? 'md:pl-[64px]' : 'md:pl-[320px]'}
          /* KEY FIX: If chat, fit to screen height. If not, min-h-screen */
          ${noScrollMode ? 'h-screen overflow-hidden' : 'min-h-screen'}
      `}>

        <main className={` w-full  relative ${noScrollMode ? 'flex-1 h-full flex flex-col overflow-hidden' : 'flex-1 overflow-y-auto overflow-x-hidden'}`}>
          <div className={` w-full h-full ${noScrollMode ? '' : 'px-4 pb-10'}`}>
            {route === "home" && <Home onStart={handleHomeStart} />}
            {route === "chat" && (
               <Chat
                 conv={conversations.find((c) => c.id === activeConvId) || null}
                 conversations={conversations}
                 onSend={(msg) => sendFromChat(msg)}
                 onOpenConversation={openConversation}
                 onNewConversation={() => createNewConversation(setShowAuth)}
                 toggleSidebar={() => handleSidebarToggle()} 
                 sidebarCollapsed={sidebarCollapsed}
               />
            )}
            {route === "about" && <AboutUs />}
            {route === "contact" && <ContactUs />}
            {route === "business" && <Business />}
            {route === "news" && <News />}
            {route === "settings" && <ProfileSettings />}
          </div>
        </main>
      </div>

      <AuthModal 
        open={showAuth} 
        onClose={() => setShowAuth(false)} 
      />

    </div>
  );
}